import React, { useState, useRef, useCallback, useEffect } from 'react';
import superjson from 'superjson';
import { Radio, Square, Mic, MicOff, Code2 } from 'lucide-react';
import { Room, RoomEvent, createLocalAudioTrack, LocalAudioTrack } from 'livekit-client';
import { Button } from './Button';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { Avatar, AvatarFallback, AvatarImage } from './Avatar';
import styles from './AiMeetingButton.module.css';

interface LiveKitMeetingProps {
  sessionId: string;
  disabled?: boolean;
  userId: string;
  userName: string;
  userAvatar?: string;
  onMeetingStart?: () => void;
  onMeetingStop?: () => void;
  /** Inject a message from the chat input into the meeting conversation */
  chatMessage?: string;
  onChatMessageConsumed?: () => void;
}

interface MeetingParticipant {
  id: string;
  name: string;
  icon?: string;
  personaId?: string;
  isSpeaking: boolean;
}

// ---------------------------------------------------------------------------
// Multi-provider voice system
// ---------------------------------------------------------------------------

type VoiceProvider = 'cloudflare' | 'elevenlabs' | 'murf' | 'inworld' | 'kitten';

interface VoiceAssignment {
  provider: VoiceProvider;
  voiceId: string;
  styleVariant: number;
}

// Cloudflare Workers AI (Deepgram Aura-1) — built-in, no API key, 12 voices
const CLOUDFLARE_VOICES = [
  'asteria', 'luna', 'stella', 'athena', 'hera',  // female
  'orion', 'arcas', 'orpheus', 'angus', 'zeus', 'perseus', 'helios', // male
];

// ElevenLabs voices — natural, distinct, mix of male/female
const ELEVENLABS_VOICES = [
  'Rachel', 'Drew', 'Clyde', 'Paul', 'Domi',
  'Dave', 'Fin', 'Sarah', 'Antoni', 'Thomas',
  'Charlie', 'George', 'Emily', 'Elli', 'Callum',
];

const MURF_VOICES = [
  'en-US-cooper', 'en-US-alina', 'en-US-imani', 'en-US-terrell',
  'en-US-zion', 'en-US-riley', 'en-US-samantha', 'en-US-denzel',
  'en-US-maya', 'en-US-marcus', 'en-US-brianna', 'en-US-jordan',
];

const INWORLD_VOICES = [
  'Dennis', 'Sarah', 'James', 'Emma', 'Michael',
  'Olivia', 'David', 'Sophie', 'Rafael', 'Omar',
  'Clive', 'Luna', 'Ashley', 'Deborah', 'Wendy', 'Timothy',
];

const KITTEN_VOICES = [
  'aura-asteria-en', 'aura-luna-en', 'aura-stella-en',
  'aura-athena-en', 'aura-hera-en', 'aura-orion-en',
  'aura-arcas-en', 'aura-perseus-en', 'aura-angus-en',
  'aura-orpheus-en', 'aura-helios-en', 'aura-zeus-en',
];

const stableHash = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const PARTY_STYLES: Array<{ rate: number; pitchSemitones: number }> = [
  { rate: 0, pitchSemitones: 0 },
  { rate: 5, pitchSemitones: 1 },
  { rate: -3, pitchSemitones: -1 },
  { rate: 8, pitchSemitones: 2 },
];

const voiceCache = new Map<string, VoiceAssignment>();

function assignVoice(personaId: string, partyMode: boolean): VoiceAssignment {
  const cacheKey = `${personaId}:${partyMode}`;
  if (voiceCache.has(cacheKey)) return voiceCache.get(cacheKey)!;

  const h = stableHash(personaId);
  // Cloudflare Workers AI first — built-in, no API key, always available
  const provider: VoiceProvider = 'cloudflare';

  const h2 = stableHash(personaId + ':voice');
  let voiceId: string;
  // Each agent gets a unique Cloudflare voice
  voiceId = CLOUDFLARE_VOICES[h2 % CLOUDFLARE_VOICES.length];

  const styleVariant = partyMode ? (stableHash(personaId + ':style') % PARTY_STYLES.length) : 0;
  const assignment: VoiceAssignment = { provider, voiceId, styleVariant };
  voiceCache.set(cacheKey, assignment);
  return assignment;
}

const getAvatar = (id: string, name: string) =>
  `https://robohash.org/${stableHash(`${id}-${name}`)}.png?set=set1&size=48x48`;

// ---------------------------------------------------------------------------
// TTS helpers
// ---------------------------------------------------------------------------

async function getToken(sessionId: string, identity: string, name: string, isAgent = false) {
  const res = await fetch('/_api/livekit/token', {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: superjson.stringify({
      roomName: `meeting-${sessionId}`,
      participantIdentity: identity,
      participantName: name,
      isAgent,
    }),
  });
  const raw = await res.text();
  try { return superjson.parse(raw) as any; } catch { return JSON.parse(raw); }
}

/** Voxtral state — skip after first failure, retry after 10 min */
let voxtralHealthy = true; // optimistic — try once
let voxtralLastFail = 0;
const VOXTRAL_RETRY_INTERVAL = 10 * 60 * 1000; // 10 min after failure before retrying

function isVoxtralWorthTrying(): boolean {
  if (voxtralHealthy) return true;
  // If it failed, only retry after 10 min
  return Date.now() - voxtralLastFail > VOXTRAL_RETRY_INTERVAL;
}

/** Track which providers are down so we skip them instantly */
const providerDown: Record<string, number> = {};
const PROVIDER_RETRY_MS = 10 * 60 * 1000; // 10 min

function isProviderUp(name: string): boolean {
  const failTime = providerDown[name];
  if (!failTime) return true;
  if (Date.now() - failTime > PROVIDER_RETRY_MS) {
    delete providerDown[name];
    return true;
  }
  return false;
}

function markProviderDown(name: string) {
  providerDown[name] = Date.now();
  console.log(`AIMEETING TTS: ${name} marked DOWN — skipping for 10 min`);
}

/** Try a single TTS fetch, return audio or null */
async function tryTTS(
  provider: string,
  endpoint: string,
  body: Record<string, any>,
): Promise<ArrayBuffer | null> {
  if (!isProviderUp(provider)) return null;
  try {
    const res = await fetch(endpoint, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: superjson.stringify(body),
    });
    if (res.ok) {
      const buf = await res.arrayBuffer();
      if (buf.byteLength > 100) return buf;
    }
    markProviderDown(provider);
  } catch {
    markProviderDown(provider);
  }
  return null;
}

async function fetchTTSForAssignment(
  text: string,
  assignment: VoiceAssignment,
  personaId?: string,
): Promise<ArrayBuffer | null> {
  const clean = text.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/`[^`]+`/g, '').replace(/[#_~>]/g, '').trim();
  if (clean.length < 5) return null;

  // Build provider-specific request configs
  const murfVoice = assignment.voiceId && MURF_VOICES.includes(assignment.voiceId)
    ? assignment.voiceId
    : MURF_VOICES[personaId ? stableHash(personaId + ':murf') % MURF_VOICES.length : 0];

  const kittenVoice = assignment.voiceId && KITTEN_VOICES.includes(assignment.voiceId)
    ? assignment.voiceId
    : KITTEN_VOICES[personaId ? stableHash(personaId + ':kitten') % KITTEN_VOICES.length : 0];

  const inworldVoice = assignment.voiceId && INWORLD_VOICES.includes(assignment.voiceId)
    ? assignment.voiceId
    : INWORLD_VOICES[personaId ? stableHash(personaId + ':inworld') % INWORLD_VOICES.length : 0];

  const providers: Array<{ name: string; endpoint: string; body: Record<string, any> }> = [];

  // Cloudflare Workers AI — primary TTS, always available, no API key needed
  const cfVoice = assignment.voiceId || CLOUDFLARE_VOICES[personaId ? stableHash(personaId + ':cf') % CLOUDFLARE_VOICES.length : 0];

  // 1. Primary: Cloudflare Workers AI (built-in)
  providers.push({ name: 'cloudflare', endpoint: '/_api/tts/cloudflare', body: { text: clean, speaker: cfVoice } });

  // 2. Fallback chain: ElevenLabs -> Murf -> Kitten -> Inworld
  const elevenlabsVoice = ELEVENLABS_VOICES[personaId ? stableHash(personaId + ':elevenlabs') % ELEVENLABS_VOICES.length : 0];
  providers.push({ name: 'elevenlabs', endpoint: '/_api/tts/elevenlabs', body: { text: clean, voice: elevenlabsVoice } });
  providers.push({ name: 'murf', endpoint: '/_api/tts/murf', body: { text: clean, voiceId: murfVoice, format: 'MP3', rate: 15 } });
  providers.push({ name: 'kitten', endpoint: '/_api/tts/kitten', body: { text: clean, voice: kittenVoice, speed: 1.0 } });
  providers.push({ name: 'inworld', endpoint: '/_api/tts/inworld', body: { text: clean, voiceId: inworldVoice } });

  // Try each provider in order — first success wins
  for (const p of providers) {
    const audio = await tryTTS(p.name, p.endpoint, p.body);
    if (audio) return audio;
  }

  // All API providers failed — return null, browser TTS will be used as fallback
  return null;
}

/** Cached sorted voice list — best quality first */
let cachedVoices: SpeechSynthesisVoice[] | null = null;

function getBestVoices(): SpeechSynthesisVoice[] {
  if (cachedVoices && cachedVoices.length > 0) return cachedVoices;
  const all = speechSynthesis.getVoices();
  if (all.length === 0) {
    // Voices not loaded yet — register listener for next call
    speechSynthesis.addEventListener('voiceschanged', () => { cachedVoices = null; }, { once: true });
    return [];
  }

  // Score voices: higher = better quality, more natural
  const scored = all
    .filter((v) => v.lang.startsWith('en'))
    .map((v) => {
      let score = 0;
      const name = v.name.toLowerCase();
      // Google voices are neural/natural — strongly prefer
      if (name.includes('google')) score += 100;
      // Microsoft Online voices are decent
      if (name.includes('online') || name.includes('natural')) score += 80;
      // "Enhanced" or "Premium" voices
      if (name.includes('enhanced') || name.includes('premium')) score += 70;
      // Samantha, Alex, etc. are decent on Mac
      if (name.includes('samantha') || name.includes('alex')) score += 50;
      // Prefer US English
      if (v.lang === 'en-US' || v.lang === 'en_US') score += 20;
      // Avoid explicitly robotic ones
      if (name.includes('espeak') || name.includes('festival')) score -= 50;
      // Remote voices tend to be higher quality
      if (!v.localService) score += 10;
      return { voice: v, score };
    })
    .sort((a, b) => b.score - a.score);

  cachedVoices = scored.map((s) => s.voice);
  console.log(`AIMEETING TTS: Best browser voices: ${cachedVoices.slice(0, 5).map((v) => v.name).join(', ')}`);
  return cachedVoices;
}

/** Browser built-in TTS — picks the most natural voice available */
function speakWithBrowserTTS(text: string, personaId?: string): Promise<void> {
  return new Promise(async (resolve) => {
    if (!('speechSynthesis' in window)) { resolve(); return; }
    speechSynthesis.cancel();

    // Wait for voices to load (up to 2s) if not available yet
    let voices = getBestVoices();
    if (voices.length === 0) {
      await new Promise<void>((r) => {
        const h = () => { speechSynthesis.removeEventListener('voiceschanged', h); r(); };
        speechSynthesis.addEventListener('voiceschanged', h);
        setTimeout(r, 2000);
      });
      cachedVoices = null;
      voices = getBestVoices();
    }

    const utterance = new SpeechSynthesisUtterance(text);
    if (voices.length > 0 && personaId) {
      // Pick from top 5 best voices for variety, not from the whole list
      const topVoices = voices.slice(0, Math.min(8, voices.length));
      const idx = stableHash(personaId) % topVoices.length;
      utterance.voice = topVoices[idx];
    } else if (voices.length > 0) {
      utterance.voice = voices[0];
    }
    utterance.lang = 'en-US';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;
    utterance.onend = () => resolve();
    utterance.onerror = () => { console.warn('Browser TTS error'); resolve(); };
    setTimeout(() => {
      speechSynthesis.speak(utterance);
      setTimeout(() => {
        if (speechSynthesis.speaking) return;
        console.warn('Browser TTS did not start, skipping');
        resolve();
      }, 500);
    }, 50);
  });
}

/** Stop any browser TTS in progress */
function stopBrowserTTS() {
  if ('speechSynthesis' in window) speechSynthesis.cancel();
}

function playAudioBuffer(audio: ArrayBuffer): { promise: Promise<void>; stop: () => void } {
  // Detect format from header — WAV starts with "RIFF", otherwise assume MP3
  const header = new Uint8Array(audio.slice(0, 4));
  const isWav = header[0] === 0x52 && header[1] === 0x49 && header[2] === 0x46 && header[3] === 0x46;
  const blob = new Blob([audio], { type: isWav ? 'audio/wav' : 'audio/mpeg' });
  const url = URL.createObjectURL(blob);
  const el = new Audio(url);
  let stopped = false;

  const promise = new Promise<void>((resolve) => {
    el.onended = () => { URL.revokeObjectURL(url); resolve(); };
    el.onerror = () => { URL.revokeObjectURL(url); resolve(); };
    el.play().catch(() => { URL.revokeObjectURL(url); resolve(); });
  });

  const stop = () => {
    if (stopped) return;
    stopped = true;
    el.pause();
    el.currentTime = 0;
    URL.revokeObjectURL(url);
  };

  return { promise, stop };
}

async function publishAudioToRoom(room: Room, audioData: ArrayBuffer): Promise<void> {
  const ctx = new AudioContext();
  const buffer = await ctx.decodeAudioData(audioData);
  const dest = ctx.createMediaStreamDestination();
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(dest);
  source.start();

  const mediaTrack = dest.stream.getAudioTracks()[0];
  const lkTrack = new LocalAudioTrack(mediaTrack);

  await room.localParticipant.publishTrack(lkTrack);

  await new Promise<void>((resolve) => {
    source.onended = () => {
      try { room.localParticipant.unpublishTrack(lkTrack); } catch {}
      try { lkTrack.stop(); } catch {}
      try { ctx.close(); } catch {}
      resolve();
    };
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const LiveKitMeeting: React.FC<LiveKitMeetingProps> = ({
  sessionId, disabled, userId, userName, userAvatar, onMeetingStart, onMeetingStop,
  chatMessage, onChatMessageConsumed,
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [participants, setParticipants] = useState<MeetingParticipant[]>([]);
  const [isMicOn, setIsMicOn] = useState(false);
  const [status, setStatus] = useState('');
  const [currentSpeaker, setCurrentSpeaker] = useState<string | null>(null);
  const [partyMode, setPartyMode] = useState(true);
  const [codingActivity, setCodingActivity] = useState<string[]>([]);
  const stopRef = useRef(false);
  const contextRef = useRef<Array<{ personaName: string; message: string }>>([]);
  const humanRoomRef = useRef<Room | null>(null);
  /** Allows interrupting current agent audio playback */
  const currentPlaybackRef = useRef<{ stop: () => void } | null>(null);
  /** Web Speech API recognition for operator STT */
  const recognitionRef = useRef<any>(null);
  /** Queued operator utterances that need immediate response */
  const operatorQueueRef = useRef<string[]>([]);
  /** Flag: an operator-triggered response round is in progress */
  const respondingToOperatorRef = useRef(false);

  const refreshParticipants = useCallback(() => {
    const room = humanRoomRef.current;
    if (!room) return;
    const parts: MeetingParticipant[] = [];
    const addP = (identity: string, name: string, speaking: boolean) => {
      parts.push({ id: identity, name, isSpeaking: speaking, personaId: identity, icon: undefined });
    };
    addP(room.localParticipant.identity, room.localParticipant.name || userName, room.localParticipant.isSpeaking);
    room.remoteParticipants.forEach((p) => addP(p.identity, p.name || p.identity, p.isSpeaking));
    setParticipants(parts);
  }, [userName]);

  /**
   * Fetch a round of responses from the backend and play them.
   * If operatorMessage is provided, it's injected so agents respond to the user.
   */
  const fetchAndPlayRound = useCallback(async (
    isFirst: boolean,
    operatorMessage?: string,
  ) => {
    if (stopRef.current) return;

    try {
      const payload: any = {
        sessionId, tone: 'casual',
        isContinuation: !isFirst,
        previousMessages: contextRef.current.slice(-12),
        executeCode: true, // always try to code
      };
      if (operatorMessage) {
        payload.userMessage = operatorMessage;
      }

      const res = await fetch('/_api/chat/start-podcast', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: superjson.stringify(payload),
      });
      const raw = await res.text();
      let data: any;
      try { data = superjson.parse(raw); } catch { data = JSON.parse(raw); }
      if (!data.success) return;

      const msgs = data.messages || [];
      const filesChanged = data.filesChanged || [];
      console.log(`AIMEETING: Round got ${msgs.length} messages, ${filesChanged.length} files changed`);

      if (filesChanged.length > 0) {
        setCodingActivity(filesChanged.map((f: string) => f.split(':').pop() || f).slice(0, 5));
        setTimeout(() => setCodingActivity([]), 8000);
        for (const entry of filesChanged) {
          const parts = entry.split(':');
          const agent = parts.length >= 3 ? parts[0] : 'AI Team';
          const action = parts.length >= 3 ? parts[1] : 'wrote';
          const file = parts.length >= 3 ? parts.slice(2).join(':') : entry;
          window.dispatchEvent(new CustomEvent('tasking.aiActivity', {
            detail: { agent, file, action },
          }));
        }
      }

      if (msgs.length === 0) return;

      // Pre-fetch ALL TTS audio in parallel while we play sequentially
      // This eliminates dead air between speakers — like a real meeting
      const ttsPromises = msgs.map((msg: any) => {
        const voice = assignVoice(msg.personaId, partyMode);
        return Promise.race([
          fetchTTSForAssignment(msg.message, voice, msg.personaId),
          new Promise<null>((r) => setTimeout(() => r(null), 6000)),
        ]).catch(() => null);
      });

      // Start fetching all TTS immediately
      const audioBuffers = await Promise.all(ttsPromises);

      for (let mi = 0; mi < msgs.length; mi++) {
        const msg = msgs[mi];
        if (stopRef.current) break;
        if (operatorQueueRef.current.length > 0 && !operatorMessage) break;

        console.log(`AIMEETING: ${msg.personaName}: "${msg.message.slice(0, 60)}"`);
        setCurrentSpeaker(msg.personaId);

        if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} }

        // Use pre-fetched audio (already loaded while previous speaker was talking)
        const audio = audioBuffers[mi];

        if (audio) {
          const playback = playAudioBuffer(audio);
          currentPlaybackRef.current = playback;
          await playback.promise;
          currentPlaybackRef.current = null;
        } else {
          await speakWithBrowserTTS(msg.message, msg.personaId);
        }

        if (recognitionRef.current && !stopRef.current) {
          try { recognitionRef.current.start(); } catch {}
        }

        contextRef.current.push({ personaName: msg.personaName, message: msg.message });
        setCurrentSpeaker(null);
        await new Promise((r) => setTimeout(r, 80));
      }
    } catch (err) {
      console.error('[Meeting] Round error:', err);
    }
  }, [sessionId, partyMode]);

  const start = useCallback(async () => {
    if (isRunning || !userId) return;
    stopRef.current = false;
    operatorQueueRef.current = [];
    respondingToOperatorRef.current = false;
    voiceCache.clear();
    // Reset TTS provider failure cache — fresh start every meeting
    for (const key of Object.keys(providerDown)) delete providerDown[key];
    contextRef.current = [];
    setIsRunning(true);
    setStatus('Starting...');
    setCodingActivity([]);
    // Kick off voice loading (async — completes in background before first TTS needed)
    if ('speechSynthesis' in window) {
      speechSynthesis.getVoices();
      cachedVoices = null;
    }

    // START STT SYNCHRONOUSLY — must happen in the click handler context (user gesture)
    // before any await, or the browser blocks mic access
    try {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = false;
        recognition.lang = 'en-US';
        recognition.onresult = (event: any) => {
          for (let i = event.resultIndex; i < event.results.length; i++) {
            if (event.results[i].isFinal) {
              const transcript = event.results[i][0].transcript.trim();
              if (transcript.length > 2) {
                console.log(`AIMEETING STT: Operator said: "${transcript}"`);
                contextRef.current.push({ personaName: userName, message: transcript });
                operatorQueueRef.current.push(transcript);
                setStatus(`You: "${transcript.slice(0, 50)}${transcript.length > 50 ? '...' : ''}"`);
                stopBrowserTTS();
                if (currentPlaybackRef.current) {
                  currentPlaybackRef.current.stop();
                  currentPlaybackRef.current = null;
                }
              }
            }
          }
        };
        recognition.onend = () => {
          if (!stopRef.current) try { recognition.start(); } catch {}
        };
        recognition.onerror = (e: any) => {
          console.warn('AIMEETING STT error:', e.error);
          if (e.error !== 'aborted' && !stopRef.current) {
            setTimeout(() => { try { recognition.start(); } catch {} }, 500);
          }
        };
        recognition.start();
        recognitionRef.current = recognition;
        setIsMicOn(true);
        setStatus('Mic active — speak anytime');
        console.log('AIMEETING: STT started successfully');
      } else {
        console.warn('AIMEETING: Web Speech API not available');
        setStatus('Speech recognition not available');
      }
    } catch (sttErr) {
      console.error('AIMEETING: STT failed to start:', sttErr);
    }

    try {
      onMeetingStart?.();

      // Fire LiveKit connection + AI team fetch IN PARALLEL
      const [podResult, roomResult] = await Promise.allSettled([
        // AI team — the critical path
        (async () => {
          const podRes = await fetch('/_api/chat/start-podcast', {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: superjson.stringify({ sessionId, tone: 'casual', executeCode: true }),
          });
          const podRaw = await podRes.text();
          try { return superjson.parse(podRaw) as any; } catch { return JSON.parse(podRaw); }
        })(),
        // LiveKit room — nice to have but not blocking
        (async () => {
          try {
            const tokenData = await getToken(sessionId, `user-${userId}`, userName);
            if (tokenData.error) return null;
            const { token, url } = tokenData;
            const room = new Room();
            await room.connect(url, token);
            humanRoomRef.current = room;
            try {
              const mic = await createLocalAudioTrack();
              await room.localParticipant.publishTrack(mic);
            } catch {}
            room.on(RoomEvent.ParticipantConnected, refreshParticipants);
            room.on(RoomEvent.ParticipantDisconnected, refreshParticipants);
            room.on(RoomEvent.ActiveSpeakersChanged, refreshParticipants);

            // Handle disconnections with auto-reconnect (critical for mobile)
            room.on(RoomEvent.Disconnected, async () => {
              if (stopRef.current) return; // intentional stop, don't reconnect
              console.warn('AIMEETING: Room disconnected, attempting reconnect...');
              setStatus('Reconnecting...');
              let retries = 0;
              const maxRetries = 5;
              const reconnect = async () => {
                if (stopRef.current || retries >= maxRetries) {
                  if (retries >= maxRetries) setStatus('Connection lost');
                  return;
                }
                retries++;
                const delay = Math.min(1000 * Math.pow(2, retries - 1), 10000);
                await new Promise((r) => setTimeout(r, delay));
                try {
                  const freshToken = await getToken(sessionId, `user-${userId}`, userName);
                  if (freshToken.error || stopRef.current) return;
                  await room.connect(freshToken.url, freshToken.token);
                  humanRoomRef.current = room;
                  setStatus('Reconnected');
                  refreshParticipants();
                  // Re-publish mic if it was on
                  if (isMicOn) {
                    try {
                      const mic = await createLocalAudioTrack();
                      await room.localParticipant.publishTrack(mic);
                    } catch {}
                  }
                } catch (err) {
                  console.warn(`AIMEETING: Reconnect attempt ${retries}/${maxRetries} failed`, err);
                  reconnect();
                }
              };
              reconnect();
            });

            refreshParticipants();
            return room;
          } catch { return null; }
        })(),
      ]);

      const pod = podResult.status === 'fulfilled' ? podResult.value : null;
      if (!pod?.success) { setStatus(`Error: ${pod?.error || 'Failed to start'}`); return; }

      // 3. Register participants — always include the user + all agent personas
      const personas = pod.participants || [];
      const userParticipant: MeetingParticipant = {
        id: `user-${userId}`,
        name: userName,
        isSpeaking: false,
        personaId: `user-${userId}`,
        icon: undefined,
      };
      const agentParticipants: MeetingParticipant[] = personas.map((p: any) => ({
        id: `agent-${p.personaId || p.id}`,
        name: p.name,
        isSpeaking: false,
        personaId: p.personaId || p.id,
        icon: undefined,
      }));
      setParticipants([userParticipant, ...agentParticipants]);
      setStatus(`${personas.length} agents joined`);

      // Show coding activity from first round
      if (pod.filesChanged?.length > 0) {
        setCodingActivity(pod.filesChanged.slice(0, 5));
        setTimeout(() => setCodingActivity([]), 8000);
        for (const file of pod.filesChanged) {
          window.dispatchEvent(new CustomEvent('tasking.aiActivity', {
            detail: { agent: 'AI Team', file, action: 'wrote' },
          }));
        }
      }

      // 4. Speak initial messages
      const initMsgs = pod.messages || [];
      console.log(`AIMEETING: Got ${initMsgs.length} initial messages from ${personas.length} agents`);

      for (const msg of initMsgs) {
        if (stopRef.current) break;
        if (operatorQueueRef.current.length > 0) break;

        console.log(`AIMEETING: ${msg.personaName}: "${msg.message.slice(0, 60)}..."`);
        setCurrentSpeaker(msg.personaId);
        setStatus(`${msg.personaName} is speaking...`);

        // Pause STT while agent speaks
        if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} }

        // Get TTS with 8s timeout — never hang forever
        let audio: ArrayBuffer | null = null;
        try {
          const voice = assignVoice(msg.personaId, partyMode);
          audio = await Promise.race([
            fetchTTSForAssignment(msg.message, voice, msg.personaId),
            new Promise<null>((r) => setTimeout(() => r(null), 8000)),
          ]);
        } catch {}

        if (audio) {
          const playback = playAudioBuffer(audio);
          currentPlaybackRef.current = playback;
          await playback.promise;
          currentPlaybackRef.current = null;
        } else {
          console.log(`AIMEETING: Using browser TTS for ${msg.personaName}`);
          await speakWithBrowserTTS(msg.message, msg.personaId);
        }

        // Resume STT
        if (recognitionRef.current && !stopRef.current) {
          try { recognitionRef.current.start(); } catch {}
        }

        contextRef.current.push({ personaName: msg.personaName, message: msg.message });
        setCurrentSpeaker(null);
        await new Promise((r) => setTimeout(r, 80));
      }

      // 5. Continuous conversation loop
      setStatus('Meeting active');
      while (!stopRef.current) {
        // Check if operator said something — respond immediately
        if (operatorQueueRef.current.length > 0) {
          const utterances = operatorQueueRef.current.splice(0);
          const combined = utterances.join(' ');
          respondingToOperatorRef.current = true;
          setStatus(`Responding to: "${combined.slice(0, 40)}..."`);

          // Fast response — voice only, no code gen, so it doesn't block for 30s
          try {
            const res = await fetch('/_api/chat/start-podcast', {
              method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: superjson.stringify({
                sessionId, tone: 'casual',
                isContinuation: true,
                previousMessages: contextRef.current.slice(-12),
                userMessage: combined,
                executeCode: false, // skip code gen for fast response
              }),
            });
            const raw = await res.text();
            let data: any;
            try { data = superjson.parse(raw); } catch { data = JSON.parse(raw); }
            if (data?.success && data.messages?.length > 0) {
              for (const msg of data.messages) {
                if (stopRef.current) break;
                console.log(`AIMEETING: ${msg.personaName} responds to boss: "${msg.message.slice(0, 60)}"`);
                setCurrentSpeaker(msg.personaId);
                if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} }
                let audio: ArrayBuffer | null = null;
                try {
                  const voice = assignVoice(msg.personaId, partyMode);
                  audio = await Promise.race([
                    fetchTTSForAssignment(msg.message, voice, msg.personaId),
                    new Promise<null>((r) => setTimeout(() => r(null), 8000)),
                  ]);
                } catch {}
                if (audio) {
                  const playback = playAudioBuffer(audio);
                  currentPlaybackRef.current = playback;
                  await playback.promise;
                  currentPlaybackRef.current = null;
                } else {
                  await speakWithBrowserTTS(msg.message, msg.personaId);
                }
                if (recognitionRef.current && !stopRef.current) {
                  try { recognitionRef.current.start(); } catch {}
                }
                contextRef.current.push({ personaName: msg.personaName, message: msg.message });
                setCurrentSpeaker(null);
              }
            }
          } catch (err) {
            console.error('[Meeting] Operator response error:', err);
          }

          respondingToOperatorRef.current = false;
          setStatus('Meeting active');
          continue;
        }

        // Continuous flow — fire next round immediately, no dead air
        // Pre-fetch next round WHILE current round is still speaking
        if (stopRef.current) break;
        if (operatorQueueRef.current.length > 0) continue;

        setStatus('Meeting active');

        // Start fetching next round's messages immediately
        const nextRoundPromise = (async () => {
          try {
            const payload: any = {
              sessionId, tone: 'casual',
              isContinuation: true,
              previousMessages: contextRef.current.slice(-12),
              executeCode: true,
            };
            const res = await fetch('/_api/chat/start-podcast', {
              method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: superjson.stringify(payload),
            });
            const raw = await res.text();
            try { return superjson.parse(raw) as any; } catch { return JSON.parse(raw); }
          } catch { return null; }
        })();

        // Small breath between speakers — natural meeting cadence
        await new Promise((r) => setTimeout(r, 150));

        const nextData = await nextRoundPromise;
        if (nextData?.success && nextData.messages?.length > 0) {
          // Pre-fetch TTS for all messages in parallel
          const msgs = nextData.messages;
          const ttsProms = msgs.map((m: any) => {
            const v = assignVoice(m.personaId, partyMode);
            return Promise.race([
              fetchTTSForAssignment(m.message, v, m.personaId),
              new Promise<null>((r) => setTimeout(() => r(null), 6000)),
            ]).catch(() => null);
          });
          const audios = await Promise.all(ttsProms);

          // Show coding activity
          if (nextData.filesChanged?.length > 0) {
            setCodingActivity(nextData.filesChanged.map((f: string) => f.split(':').pop() || f).slice(0, 5));
            setTimeout(() => setCodingActivity([]), 8000);
            for (const entry of nextData.filesChanged) {
              const parts = entry.split(':');
              window.dispatchEvent(new CustomEvent('tasking.aiActivity', {
                detail: { agent: parts[0] || 'AI Team', file: parts.slice(2).join(':') || entry, action: parts[1] || 'wrote' },
              }));
            }
          }

          // Play each speaker — audio already loaded
          for (let i = 0; i < msgs.length; i++) {
            if (stopRef.current) break;
            if (operatorQueueRef.current.length > 0) break;
            const msg = msgs[i];
            setCurrentSpeaker(msg.personaId);
            if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch {} }

            if (audios[i]) {
              const playback = playAudioBuffer(audios[i]!);
              currentPlaybackRef.current = playback;
              await playback.promise;
              currentPlaybackRef.current = null;
            } else {
              await speakWithBrowserTTS(msg.message, msg.personaId);
            }

            if (recognitionRef.current && !stopRef.current) {
              try { recognitionRef.current.start(); } catch {}
            }
            contextRef.current.push({ personaName: msg.personaName, message: msg.message });
            setCurrentSpeaker(null);
          }
        }
      }
    } catch (err: any) {
      console.error('[Meeting]', err);
      setStatus(`Error: ${err.message || 'Connection failed'}`);
    }

    setIsRunning(false);
  }, [sessionId, userId, userName, isRunning, partyMode, refreshParticipants, fetchAndPlayRound]);

  const stop = useCallback(() => {
    stopRef.current = true;
    stopBrowserTTS();
    if (currentPlaybackRef.current) {
      currentPlaybackRef.current.stop();
      currentPlaybackRef.current = null;
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      recognitionRef.current = null;
    }
    humanRoomRef.current?.disconnect();
    humanRoomRef.current = null;
    setIsRunning(false);
    setParticipants([]);
    setIsMicOn(false);
    setStatus('');
    setCurrentSpeaker(null);
    setCodingActivity([]);
    onMeetingStop?.();
  }, [onMeetingStop]);

  const toggleMic = useCallback(() => {
    const room = humanRoomRef.current;
    if (room) {
      room.localParticipant.audioTrackPublications.forEach((pub) => {
        if (pub.track) { isMicOn ? pub.track.mute() : pub.track.unmute(); }
      });
    }
    if (isMicOn) {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {}
      }
      setStatus('Muted');
    } else {
      if (recognitionRef.current) {
        try { recognitionRef.current.start(); } catch {}
      }
      setStatus('Listening... speak anytime');
    }
    setIsMicOn(!isMicOn);
  }, [isMicOn]);

  // Bridge: chat messages inject into the meeting conversation
  useEffect(() => {
    if (chatMessage && chatMessage.trim() && isRunning) {
      console.log(`AIMEETING: Chat message injected: "${chatMessage}"`);
      contextRef.current.push({ personaName: userName, message: chatMessage.trim() });
      operatorQueueRef.current.push(chatMessage.trim());
      setStatus(`You: "${chatMessage.trim().slice(0, 50)}"`);
      // Interrupt current playback
      stopBrowserTTS();
      if (currentPlaybackRef.current) {
        currentPlaybackRef.current.stop();
        currentPlaybackRef.current = null;
      }
      onChatMessageConsumed?.();
    }
  }, [chatMessage, isRunning, userName, onChatMessageConsumed]);

  // Handle mobile tab visibility changes — pause STT when backgrounded, resume on foreground.
  // Do NOT disconnect the LiveKit room; it handles reconnection internally.
  useEffect(() => {
    const handleVisibility = () => {
      if (!isRunning) return;
      if (document.hidden) {
        // Pause speech recognition when app goes to background (mobile)
        if (recognitionRef.current) {
          try { recognitionRef.current.stop(); } catch {}
        }
      } else {
        // Resume speech recognition when app comes back to foreground
        if (recognitionRef.current && isMicOn && !stopRef.current) {
          try { recognitionRef.current.start(); } catch {}
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [isRunning, isMicOn]);

  // NOTE: Do NOT clean up on unmount — the meeting should survive tab switches.
  // Cleanup only happens via explicit stop() or when sessionId VALUE actually changes.
  // Use a ref to track the previous sessionId to avoid false triggers from parent re-renders.
  const prevSessionIdRef = useRef(sessionId);
  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      prevSessionIdRef.current = sessionId;
      if (isRunning) {
        stop();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (isRunning) {
    return (
      <div className={styles.meetingBar}>
        <div className={styles.meetingHeader}>
          <span className={styles.meetingDot} />
          <span className={styles.meetingLabel}>AI Meeting</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-sm" onClick={toggleMic}>
                {isMicOn ? <Mic size={10} className={styles.micIcon} /> : <MicOff size={10} />}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{isMicOn ? 'Mute' : 'Unmute'}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-sm" onClick={stop} className={styles.stopBtn}>
                <Square size={10} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>End Meeting</TooltipContent>
          </Tooltip>
        </div>
        {/* Current speaker name — prominent display */}
        {currentSpeaker && (() => {
          const speaker = participants.find((p) =>
            p.personaId === currentSpeaker || p.id === currentSpeaker || p.id === `agent-${currentSpeaker}`
          );
          return speaker ? (
            <div style={{ fontSize: 10, fontWeight: 600, color: 'hsl(25 90% 55%)', padding: '1px 2px', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'hsl(25 90% 55%)', display: 'inline-block', animation: 'speakGlow 1.2s ease-in-out infinite' }} />
              {speaker.name} is speaking...
            </div>
          ) : null;
        })()}
        {!currentSpeaker && status && <div style={{ fontSize: 9, color: 'hsl(220 15% 50%)', padding: '0 2px' }}>{status}</div>}
        {codingActivity.length > 0 && (
          <div style={{ fontSize: 8, color: 'hsl(142 70% 45%)', padding: '0 2px', display: 'flex', alignItems: 'center', gap: 3 }}>
            <Code2 size={8} />
            <span>{codingActivity.map((f) => f.split('/').pop()).join(', ')}</span>
          </div>
        )}
        {participants.length > 0 && (
          <div className={styles.avatarRow}>
            {participants.map((p) => {
              const isSpeaking = p.isSpeaking
                || currentSpeaker === p.id
                || currentSpeaker === p.personaId
                || p.id === `agent-${currentSpeaker}`;
              return (
                <Tooltip key={p.id}>
                  <TooltipTrigger asChild>
                    <div className={`${styles.avatarWrap} ${isSpeaking ? styles.speaking : ''}`}>
                      <Avatar className={styles.avatar}>
                        <AvatarImage src={p.id === `user-${userId}` && userAvatar ? userAvatar : getAvatar(p.id, p.name)} alt={p.name} />
                        <AvatarFallback>{p.name[0]}</AvatarFallback>
                      </Avatar>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{p.name}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon-sm" disabled={disabled} onClick={start} className={styles.startBtn}>
          <Radio size={14} />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Start AI Meeting</TooltipContent>
    </Tooltip>
  );
};
